























































































import { Breadcrumb } from '@/components/BreadCrumbs.vue';
import { Component, Vue, Watch } from 'vue-property-decorator';
import EventViewComponent from './EventViewComponent.vue';
import Container from '@/components/Container.vue';
import {
  DeleteEventDTO,
  EditedEvents,
  Event,
  EventStatus,
  EventVersion,
  JbicEntityType,
  UpdateEventStatusManageDTO,
  UpdateEventStatusSubmissionDTO
} from '@/store/modules/admin/types/admin.types';
import { Action, State } from 'vuex-class';
import { RootState } from '@/store/store';
import TableActionDropdown from '@/components/TableActionDropdown.vue';
import { ApiState } from '@/store/types/general.types';
import GeneralModal from '@/components/GeneralModal.vue';
import { ToastProgrammatic } from 'buefy';
import DeclineReasonModal from './DeclineReasonModal.vue';
import { checkJbicPermission } from '@/utils/rbac.util';
import { JbicPermissions } from '@/store/modules/roles-and-permissions/types/roles-and-permissions.types';

@Component({
  components: {
    Container,
    EventViewComponent,
    TableActionDropdown,
    GeneralModal,
    DeclineReasonModal
  }
})
export default class EventView extends Vue {
  lastReviewedEvent: EventVersion | null = null;

  @Action('admin/getJbicEventById')
  private getJbicEventById!: (eventId: number) => Promise<Event>;

  @State(({ admin }: RootState) => admin.apiState.getJbicEventById)
  private getJbicEventByIdState!: ApiState;

  @State(({ admin }: RootState) => admin.jbicEvent)
  private jbicEvent!: Event;

  @Action('admin/deleteJbicEvent')
  private deleteJbicEvent!: (event: DeleteEventDTO) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.deleteJbicEvent)
  private deleteJbicEventState!: ApiState;

  @Action('admin/submitEventForApproval')
  private submitEventForApproval!: (
    payload: UpdateEventStatusSubmissionDTO
  ) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.submitEventForApproval)
  private submitEventForApprovalState!: ApiState;

  @Action('admin/updateManagedEventStatus')
  private updateManagedEventStatus!: (params: {
    eventId: number;
    payload: UpdateEventStatusManageDTO;
  }) => Promise<void>;

  @State(({ admin }: RootState) => admin.apiState.updateManagedEventStatus)
  private updateManagedEventStatusState!: ApiState;

  @Action('admin/getEditedEvents')
  getEditedEvents!: (eventId: number) => void;

  @State(({ admin }: RootState) => admin.apiState.getEditedEvents)
  getEditedEventsApiState!: ApiState;

  @State(({ admin }: RootState) => admin.editedEvents)
  editedEvents!: EditedEvents;

  get EventViewComponent() {
    return EventViewComponent;
  }

  get eventId(): number {
    return +this.$route.params.eventId;
  }

  get eventName(): string {
    return this.jbicEvent?.title;
  }

  get groupId(): number {
    return this.jbicEvent?.group?.id;
  }

  get groupName(): string {
    return this.jbicEvent?.group?.name;
  }

  get eventStatus(): EventStatus {
    return this.jbicEvent?.status;
  }

  get eventIsEnded(): boolean {
    return this.jbicEvent?.isEnded;
  }

  public mounted(): void {
    this.getJbicEventById(this.eventId);
  }

  editEvent(): void {
    // only JBIC Admin able to edit
    if (!this.isJbicEventUpdateRole) {
      return;
    }

    this.$router.push({
      name: 'jbic-create-event',
      params: {
        eventId: this.eventId.toString(),
        groupId: this.groupId.toString(),
        changeType: 'EDIT'
      }
    });
  }
  //Return toast error
  submitEventForApprovalHandler(): void {
    // Validate all required fields are filled before submitting for approval
    if (!this.isEventCompleteForApproval()) {
      ToastProgrammatic.open({
        message: 'All fields are required before submitting for approval.',
        type: 'is-danger',
        position: 'is-top'
      });
      return;
    }
    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.AWAITING_APPROVAL,
      id: this.eventId
    };
    this.submitEventForApproval(payload);
  }

  private isEventCompleteForApproval(): boolean {
    const event = this.jbicEvent;
    return !!(
      event?.title &&
      event?.slogan &&
      event?.startAt &&
      event?.endAt &&
      event?.timezone &&
      event?.description &&
      event?.location &&
      event?.urlToEvent &&
      event?.urlToHost &&
      event?.contacts &&
      event?.bannerImageUrl &&
      event?.teaserImageUrl
    );
  }

  approveEvent(): void {
    // only JBI Admin able to approve
    if (!this.isJbicEventManageRole) {
      return;
    }

    const payload: UpdateEventStatusManageDTO = {
      status: EventStatus.APPROVED
    };
    this.updateManagedEventStatus({ eventId: this.eventId, payload });
  }

  declineEvent(): void {
    // only JBI Admin able to decline
    if (!this.isJbicEventManageRole) {
      return;
    }

    this.$buefy.modal.open({
      parent: this,
      component: DeclineReasonModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        entityType: JbicEntityType.EVENT
      },
      events: {
        confirm: (declineReason: string) => {
          const payload: UpdateEventStatusManageDTO = {
            status: EventStatus.DECLINED,
            declinedReason: declineReason
          };
          this.updateManagedEventStatus({ eventId: this.eventId, payload });
        }
      }
    });
  }

  publishEvent(): void {
    // only JBIC Admin able to publish
    if (!this.isJbicEventUpdateRole) {
      return;
    }

    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.PUBLISHED,
      id: this.eventId
    };
    this.submitEventForApproval(payload);
  }

  unpublishEvent(): void {
    // only JBIC Admin able to unpublish
    if (!this.isJbicEventUpdateRole) {
      return;
    }

    const payload: UpdateEventStatusSubmissionDTO = {
      status: EventStatus.DRAFT,
      id: this.eventId
    };
    this.submitEventForApproval(payload);
  }

  deleteEvent(): void {
    // only JBIC Admin able to delete
    if (!this.isJbicEventDeleteRole) {
      return;
    }

    this.$buefy.modal.open({
      parent: this,
      component: GeneralModal,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: 'Delete selected event?',
        modalContent:
          'This will permanently delete the selected event. This action cannot be undone.',
        confirmBtnText: 'Delete',
        cancelBtnText: 'Cancel'
      },
      events: {
        confirm: () => {
          this.deleteJbicEvent({ id: this.eventId });
        }
      }
    });
  }

  @Watch('getJbicEventByIdState')
  onGetJbicEventByIdState() {
    if (
      this.getJbicEventByIdState.error ||
      this.getJbicEventByIdState.loading ||
      !this.getJbicEventByIdState.success
    ) {
      return;
    }

    if (this.isEventStatus(EventStatus.AWAITING_APPROVAL)) {
      this.getEditedEvents(this.jbicEvent.id);
    }
  }

  @Watch('deleteJbicEventState')
  onDeleteJbicEventState() {
    if (this.deleteJbicEventState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while deleting the event. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.deleteJbicEventState.loading ||
      !this.deleteJbicEventState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event has been deleted successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.handleBackRedirect();
  }

  @Watch('submitEventForApprovalState')
  onSubmitEventForApprovalState() {
    if (this.submitEventForApprovalState.error) {
      const err: any = this.submitEventForApprovalState.error;
      ToastProgrammatic.open({
        message:
          err?.response?.data?.message ||
          'An error occurred while submitting the event for approval. Please try again.',
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.submitEventForApprovalState.loading ||
      !this.submitEventForApprovalState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicEventById(this.eventId);
  }

  @Watch('updateManagedEventStatusState')
  onUpdateManagedEventStatusState() {
    if (this.updateManagedEventStatusState.error) {
      ToastProgrammatic.open({
        message: `An error occurred while updating the event status. Please try again.`,
        type: 'is-danger',
        position: 'is-top'
      });

      return;
    }

    if (
      this.updateManagedEventStatusState.loading ||
      !this.updateManagedEventStatusState.success
    ) {
      return;
    }

    ToastProgrammatic.open({
      message: `The event status has been updated successfully.`,
      type: 'is-success',
      position: 'is-top'
    });

    this.getJbicEventById(this.eventId);
  }

  @Watch('getEditedEventsApiState')
  onGetEditedEventsApiState() {
    if (
      this.getEditedEventsApiState.loading ||
      !this.getEditedEventsApiState.success ||
      this.getEditedEventsApiState.error
    ) {
      return;
    }

    this.lastReviewedEvent = this.editedEvents.lastReviewEvent;
  }

  handleBackRedirect() {
    this.$router.go(-1);
  }

  isEventStatus(status: EventStatus): boolean {
    return this.eventStatus === status;
  }

  get isJbicEventCreateRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_CREATE, this.groupId);
  }

  get isJbicEventUpdateRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_UPDATE, this.groupId);
  }

  get isJbicEventDeleteRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_DELETE, this.groupId);
  }

  get isJbicEventViewRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_READ, this.groupId);
  }

  get isJbicEventManageRole(): boolean {
    return checkJbicPermission(JbicPermissions.EVENT_MANAGE, this.groupId);
  }

  get breadcrumbs(): Breadcrumb[] {
    const breadcrumbs: Breadcrumb[] = [];

    breadcrumbs.push({
      route: { name: 'jbic-management' },
      text: 'JBIC Management'
    });

    if (this.groupId && this.groupName) {
      breadcrumbs.push({
        route: {
          name: 'jbic-group',
          params: { groupId: this.groupId.toString() }
        },
        text: this.groupName
      });
    }

    const current: Breadcrumb = {
      text: this.eventName
    };
    breadcrumbs.push(current);

    return breadcrumbs;
  }
}
